import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  Mail,
  Loader2,
  Smartphone,
  Save,
  X,
  CheckCircle2,
  Edit3,
  Shield,
  ShieldCheck,
  PhoneCall,
  ChevronRight,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import Swal from "sweetalert2";
import imageCompression from "browser-image-compression";
import api, { IMAGE_BASE_URL } from "../api/Api.js";
import Loader from "../components/Loader2.jsx";

const ProfilePage = () => {
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [tempValue, setTempValue] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const otpRefs = [useRef(), useRef(), useRef(), useRef()];
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    customerId: "",
    email: "",
    phone: "",
    photo: null,
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/customer/profile");
      const p = res.data;
      setFormData({
        fullName: p.fullName || "",
        customerId: p.customerId || "",
        email: p.email || "",
        phone: p.phone || "",
        photo: null,
      });
      if (p.cust_img)
        setPreview(
          `${IMAGE_BASE_URL}${p.cust_img.startsWith("/") ? p.cust_img : `/${p.cust_img}`}`
        );
    } catch {
      toast.error("Error loading profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      });
      setFormData((p) => ({ ...p, photo: compressed }));
      setPreview(URL.createObjectURL(compressed));
    } catch {
      toast.error("Image error");
    }
  };

  const handleSaveProfile = async () => {
    setUpdating(true);
    const data = new FormData();
    if (formData.photo) data.append("photo", formData.photo);

    try {
      const res = await api.put("/customer/update-complete-profile", data);

      Swal.fire({
        icon: "success",
        title: "Saved!",
        text: res.data?.message,
        timer: 1500,
        showConfirmButton: false,
      }).then(() => {
        navigate("/dashboard");
      });
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenModal = (type, value) => {
    setModalType(type);
    setTempValue(value || "");
    setOtpSent(false);
    setOtpValue("");
    setOtpDigits(["", "", "", ""]);
  };

  const handleRequestOtp = async () => {
    if (!tempValue) return toast.error(`Enter valid ${modalType}`);
    setOtpLoading(true);
    try {
      const endpoint =
        modalType === "phone"
          ? "/customer/request-phone-change"
          : "/customer/request-email-change";
      const payload =
        modalType === "phone"
          ? { newMobileNumber: tempValue }
          : { newEmail: tempValue };
      const res = await api.post(endpoint, payload);
      if (res.data.dev_otp) toast.success(`OTP: ${res.data.dev_otp}`);
      setOtpSent(true);
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (error) {
      console.error("OTP API Error:", error.response?.data || error.message);
      const backendMessage = error.response?.data?.message || "Failed to send OTP";
      toast.error(backendMessage);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpDigit = (i, val) => {
    const v = val.replace(/\D/g, "").slice(-1);
    const digits = [...otpDigits];
    digits[i] = v;
    setOtpDigits(digits);
    setOtpValue(digits.join(""));
    if (v && i < 3) otpRefs[i + 1].current?.focus();
    if (!v && i > 0) otpRefs[i - 1].current?.focus();
  };

  const handleVerifyOtp = async () => {
    const code = otpDigits.join("");
    if (code.length !== 4) return toast.error("Enter 4-digit OTP");
    setOtpLoading(true);
    try {
      const endpoint =
        modalType === "phone"
          ? "/customer/verify-phone-change"
          : "/customer/verify-email-change";
      const payload =
        modalType === "phone"
          ? { newMobileNumber: tempValue, otp: code }
          : { newEmail: tempValue, otp: code };
      await api.post(endpoint, payload);
      setFormData((p) => ({
        ...p,
        [modalType === "phone" ? "phone" : "email"]: tempValue,
      }));
      toast.success("Updated successfully!");
      setModalType(null);
    } catch {
      toast.error("Invalid OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const getInitials = (name) =>
    name
      ? name
          .split(" ")
          .map((w) => w[0])
          .join("")
          .slice(0, 2)
          .toUpperCase()
      : "U";

  if (loading) return <Loader />;

  return (
    <div
      className="bg-light"
      style={{
        minHeight: "100dvh",
        fontFamily: "'Inter', 'Roboto', sans-serif",
        paddingBottom: "env(safe-area-inset-bottom)",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .press:active { 
          transform: scale(0.97); 
        }
        
        .cursor-pointer { cursor: pointer; }
        
        .native-input:focus { 
          outline: none; 
          box-shadow: none; 
          border-color: #3b82f6 !important; 
        }
        
        .list-row:active { 
          background-color: rgba(59, 130, 246, 0.05) !important; 
        }
        
        /* Glassmorphism */
        .glass-card {
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(255, 255, 255, 0.5);
        }
        
        /* Spinner Animation ONLY */
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Scrollbar Hide */
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ─── HERO HEADER ─────────────────────────────────────────────────── */}
      <div
        className="position-relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)",
          paddingTop: "32px",
          paddingBottom: "100px",
        }}
      >
        {/* Static Decorative Circles */}
        <div className="position-absolute" style={{ top: "-50px", right: "-50px", width: "200px", height: "200px", background: "rgba(255, 255, 255, 0.1)", borderRadius: "50%" }}></div>
        <div className="position-absolute" style={{ bottom: "-30px", left: "-30px", width: "150px", height: "150px", background: "rgba(255, 255, 255, 0.05)", borderRadius: "50%" }}></div>
        
        <div className="container px-3 position-relative z-1">
          <div className="text-center">
            <h1 className="text-white fw-bold mb-1" style={{ fontSize: "28px", letterSpacing: "-0.5px" }}>
              My Profile
            </h1>
            <p className="text-white text-opacity-90 mb-0 small">
              Manage your account details
            </p>
          </div>
        </div>
      </div>

      {/* ─── AVATAR CARD ──────────────────────────────────────────────────── */}
      <div className="container px-3 position-relative" style={{ marginTop: "-70px", zIndex: 10 }}>
        <div className="glass-card shadow-sm rounded-4 p-3 text-center">
          
          {/* Avatar Area */}
          <div className="d-flex justify-content-center mb-2 position-relative">
            <div className="position-relative">
              {/* Static Gradient Ring */}
              <div
                className="position-absolute top-50 start-50 translate-middle rounded-circle"
                style={{
                  width: "100px",
                  height: "100px",
                  background: "linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899)",
                  padding: "3px",
                }}
              >
                <div className="w-100 h-100 rounded-circle" style={{ background: "white" }}></div>
              </div>
              
              {/* Avatar Circle */}
              <div
                className="rounded-circle overflow-hidden d-flex align-items-center justify-content-center cursor-pointer press position-relative shadow-sm"
                onClick={() => preview && setShowImageModal(true)}
                style={{
                  width: "94px",
                  height: "94px",
                  background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                  border: "4px solid white",
                  zIndex: 2
                }}
              >
                {preview ? (
                  <img src={preview} alt="Profile" className="w-100 h-100 object-fit-cover" />
                ) : (
                  <span className="text-white fw-bold" style={{ fontSize: "32px" }}>
                    {getInitials(formData.fullName)}
                  </span>
                )}
              </div>

              {/* Camera Button */}
              <label 
                className="position-absolute rounded-circle d-flex align-items-center justify-content-center cursor-pointer press shadow-sm" 
                style={{ 
                  width: "36px", 
                  height: "36px",
                  bottom: "-4px",
                  right: "-4px",
                  background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                  border: "3px solid white",
                  zIndex: 3
                }}
              >
                <Camera size={16} className="text-white" />
                <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
              </label>
            </div>
          </div>

          {/* Name & ID */}
          <h2 className="fw-bold text-dark mb-1 mt-3" style={{ fontSize: "22px" }}>
            {formData.fullName || "Your Name"}
          </h2>

          <div className="d-inline-flex align-items-center gap-2 rounded-pill px-3 py-1 mb-2" style={{ background: "linear-gradient(135deg, #eff6ff, #dbeafe)", border: "1px solid #bfdbfe" }}>
            <Shield size={14} className="text-primary" />
            <span className="fw-semibold text-primary" style={{ fontSize: "13px" }}>
              {formData.customerId || "ID not assigned"}
            </span>
          </div>

          {/* Save Button */}
          {formData.photo && (
            <button
              className="btn w-100 rounded-4 py-2 mt-2 fw-semibold d-flex justify-content-center align-items-center gap-2 press shadow-sm"
              style={{
                background: "linear-gradient(135deg, #1e40af, #3b82f6)",
                border: "none",
                color: "white",
                fontSize: "15px"
              }}
              onClick={handleSaveProfile}
              disabled={updating}
            >
              {updating ? (
                <Loader2 size={18} className="spin" />
              ) : (
                <>
                  <Save size={18} />
                  <span>Save Profile Photo</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* ─── CONTACT DETAILS ──────────────────────────────────────────────── */}
      <div className="container px-3 mt-4">
        <div className="d-flex align-items-center justify-content-between mb-2 px-1">
          <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "14px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Contact Details
          </h6>
        </div>

        <div className="glass-card shadow-sm rounded-4 overflow-hidden">
          
          {/* Mobile Row */}
          <div 
            className="d-flex align-items-center p-3 border-bottom list-row cursor-pointer press"
            onClick={() => handleOpenModal("phone", formData.phone)}
          >
            <div 
              className="rounded-3 d-flex align-items-center justify-content-center me-3" 
              style={{ 
                width: "40px", 
                height: "40px", 
                background: "linear-gradient(135deg, #dbeafe, #bfdbfe)"
              }}
            >
              <Smartphone size={20} className="text-primary" />
            </div>
            <div className="flex-grow-1">
              <div className="text-secondary fw-semibold mb-1" style={{ fontSize: "11px", textTransform: "uppercase" }}>
                Mobile
              </div>
              <div className="fw-semibold text-dark" style={{ fontSize: "15px" }}>
                {formData.phone || "Add number"}
              </div>
            </div>
            <Edit3 size={16} className="text-secondary opacity-50" />
          </div>

          {/* Email Row */}
          <div 
            className="d-flex align-items-center p-3 list-row cursor-pointer press"
            onClick={() => handleOpenModal("email", formData.email)}
          >
            <div 
              className="rounded-3 d-flex align-items-center justify-content-center me-3" 
              style={{ 
                width: "40px", 
                height: "40px", 
                background: "linear-gradient(135deg, #dcfce7, #bbf7d0)"
              }}
            >
              <Mail size={20} className="text-success" />
            </div>
            <div className="flex-grow-1 overflow-hidden">
              <div className="text-secondary fw-semibold mb-1" style={{ fontSize: "11px", textTransform: "uppercase" }}>
                Email
              </div>
              <div className="fw-semibold text-dark text-truncate" style={{ fontSize: "15px" }}>
                {formData.email || "Add email"}
              </div>
            </div>
            <Edit3 size={16} className="text-secondary opacity-50" />
          </div>
        </div>
      </div>

      {/* ─── SECURITY & SUPPORT ─────────────────────────────────────────────── */}
      <div className="container px-3 mt-4">
        <div className="d-flex align-items-center justify-content-between mb-2 px-1">
          <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "14px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Security & Support
          </h6>
        </div>

        <div className="glass-card shadow-sm rounded-4 overflow-hidden">
          
          {/* MPIN Row */}
          <div 
            className="d-flex align-items-center p-3 border-bottom list-row cursor-pointer press"
            onClick={() => navigate("/change-mpin")}
          >
            <div 
              className="rounded-3 d-flex align-items-center justify-content-center me-3" 
              style={{ 
                width: "40px", 
                height: "40px", 
                background: "linear-gradient(135deg, #fae8ff, #f3e8ff)"
              }}
            >
              <ShieldCheck size={20} className="text-purple-600" style={{ color: "#9333ea" }} />
            </div>
            <div className="flex-grow-1">
              <div className="fw-semibold text-dark mb-1" style={{ fontSize: "15px" }}>
                Change MPIN
              </div>
              <div className="text-secondary" style={{ fontSize: "12px" }}>
                Update your security PIN
              </div>
            </div>
            <ChevronRight size={18} className="text-secondary opacity-50" />
          </div>

          {/* Contact Row */}
          <div 
            className="d-flex align-items-center p-3 list-row cursor-pointer press"
            onClick={() => navigate("/contact-office")}
          >
            <div 
              className="rounded-3 d-flex align-items-center justify-content-center me-3" 
              style={{ 
                width: "40px", 
                height: "40px", 
                background: "linear-gradient(135deg, #fef3c7, #fde68a)"
              }}
            >
              <PhoneCall size={20} className="text-warning" style={{ color: "#d97706" }} />
            </div>
            <div className="flex-grow-1">
              <div className="fw-semibold text-dark mb-1" style={{ fontSize: "15px" }}>
                Contact Support
              </div>
              <div className="text-secondary" style={{ fontSize: "12px" }}>
                We're here to help 24/7
              </div>
            </div>
            <ChevronRight size={18} className="text-secondary opacity-50" />
          </div>
        </div>
      </div>

      {/* Bottom Spacer */}
      <div className="py-5 mb-4"></div>

      {/* ─── IMAGE VIEW MODAL ─────────────────────────────────────────────── */}
      {showImageModal && preview && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-4" 
          style={{ zIndex: 2000, background: "rgba(0, 0, 0, 0.9)" }} 
          onClick={() => setShowImageModal(false)}
        >
          <div className="position-relative w-100" style={{ maxWidth: "400px" }} onClick={(e) => e.stopPropagation()}>
            <button 
              className="btn btn-light rounded-circle position-absolute d-flex align-items-center justify-content-center p-2 press" 
              style={{ top: "-50px", right: "0" }} 
              onClick={() => setShowImageModal(false)}
            >
              <X size={20} />
            </button>
            <img 
              src={preview} 
              alt="Profile" 
              className="w-100 rounded-4" 
              style={{ maxHeight: "80vh", objectFit: "contain" }} 
            />
          </div>
        </div>
      )}

      {/* ─── EDIT BOTTOM SHEET ────────────────────────────────────────────── */}
      {modalType && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100" 
          style={{ zIndex: 1500, background: "rgba(0, 0, 0, 0.6)" }} 
          onClick={() => setModalType(null)}
        >
          <div 
            className="position-absolute bottom-0 start-0 w-100 bg-white rounded-top-5" 
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }} 
            onClick={(e) => e.stopPropagation()}
          >
            
            {/* Sheet Handle */}
            <div className="d-flex justify-content-center pt-2 pb-1">
              <div className="bg-secondary rounded-pill" style={{ width: "40px", height: "4px", opacity: 0.3 }}></div>
            </div>

            <div className="px-3 pb-3">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-start mb-3 pt-2">
                <div>
                  <div className="text-secondary fw-semibold small text-uppercase" style={{ letterSpacing: "1px" }}>
                    {otpSent ? "Verification" : "Update"}
                  </div>
                  <h3 className="fw-bold m-0 text-dark" style={{ fontSize: "22px" }}>
                    {otpSent ? "Enter OTP Code" : modalType === "phone" ? "Phone Number" : "Email Address"}
                  </h3>
                </div>
                <button 
                  className="btn btn-light rounded-circle p-2 d-flex align-items-center justify-content-center border-0 press" 
                  style={{ width: "36px", height: "36px" }}
                  onClick={() => setModalType(null)}
                >
                  <X size={18} className="text-secondary" />
                </button>
              </div>

              {!otpSent ? (
                /* Step 1: Input Field */
                <div>
                  <div className="mb-3">
                    <label className="form-label text-secondary fw-semibold mb-1" style={{ fontSize: "13px" }}>
                      New {modalType === "phone" ? "mobile number" : "email address"}
                    </label>
                    <div className="d-flex align-items-center gap-2 bg-light rounded-4 p-2 border">
                      <div className="d-flex align-items-center justify-content-center px-2">
                        {modalType === "phone" ? (
                          <Smartphone size={20} className="text-secondary" />
                        ) : (
                          <Mail size={20} className="text-secondary" />
                        )}
                      </div>
                      <input
                        type={modalType === "phone" ? "tel" : "email"}
                        className="form-control native-input bg-transparent border-0 fw-semibold px-0"
                        style={{ fontSize: "16px" }}
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        autoFocus
                        placeholder={modalType === "phone" ? "Enter 10-digit number" : "your@email.com"}
                      />
                    </div>
                  </div>

                  <button
                    className={`btn w-100 rounded-4 fw-semibold press d-flex align-items-center justify-content-center gap-2 py-2 shadow-sm ${tempValue ? '' : 'opacity-50'}`}
                    style={{
                      background: tempValue ? "linear-gradient(135deg, #1e40af, #3b82f6)" : "#94a3b8",
                      border: "none",
                      color: "white",
                      fontSize: "15px"
                    }}
                    onClick={handleRequestOtp}
                    disabled={otpLoading || !tempValue}
                  >
                    {otpLoading ? (
                      <Loader2 size={20} className="spin" />
                    ) : (
                      <>
                        <span>Send OTP Code</span>
                        <ChevronRight size={18} />
                      </>
                    )}
                  </button>
                </div>
              ) : (
                /* Step 2: OTP Verification */
                <div className="text-center">
                  {/* Success Icon */}
                  <div 
                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2" 
                    style={{ 
                      width: "60px", 
                      height: "60px",
                      background: "linear-gradient(135deg, #dcfce7, #bbf7d0)"
                    }}
                  >
                    <CheckCircle2 size={30} className="text-success" />
                  </div>

                  <h6 className="fw-bold text-dark mb-1">Code Sent</h6>
                  <p className="fw-bold text-dark mb-3" style={{ fontSize: "16px" }}>
                    {tempValue}
                  </p>

                  {/* OTP Input Boxes */}
                  <div className="d-flex justify-content-center gap-2 mb-3">
                    {otpDigits.map((d, i) => (
                      <input
                        key={i}
                        ref={otpRefs[i]}
                        type="tel"
                        maxLength={1}
                        value={d}
                        className={`form-control native-input text-center fw-bold rounded-4 ${d ? 'border-primary' : 'border-secondary border-opacity-25'}`}
                        style={{ 
                          width: "52px", 
                          height: "56px",
                          fontSize: "24px",
                          background: d ? "linear-gradient(135deg, #eff6ff, #dbeafe)" : "white",
                        }}
                        onChange={(e) => handleOtpDigit(i, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !d && i > 0) otpRefs[i - 1].current?.focus();
                        }}
                      />
                    ))}
                  </div>

                  {/* Verify Button */}
                  <button
                    className={`btn w-100 rounded-4 fw-semibold press py-2 mb-2 d-flex align-items-center justify-content-center gap-2 shadow-sm ${otpDigits.join("").length === 4 ? '' : 'opacity-50'}`}
                    style={{
                      background: otpDigits.join("").length === 4 ? "linear-gradient(135deg, #1e40af, #3b82f6)" : "#94a3b8",
                      border: "none",
                      color: "white",
                      fontSize: "15px"
                    }}
                    onClick={handleVerifyOtp}
                    disabled={otpLoading || otpDigits.join("").length !== 4}
                  >
                    {otpLoading ? (
                      <Loader2 size={20} className="spin" />
                    ) : (
                      <>
                        <CheckCircle2 size={18} />
                        <span>Verify & Update</span>
                      </>
                    )}
                  </button>

                  {/* Change Number/Email Link */}
                  <button
                    className="btn btn-link text-decoration-none fw-semibold p-0"
                    style={{ color: "#64748b", fontSize: "14px" }}
                    onClick={() => { 
                      setOtpSent(false); 
                      setOtpDigits(["", "", "", ""]); 
                    }}
                  >
                    ← Change {modalType === "phone" ? "number" : "email"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;